import { useNavigate } from "react-router-dom";
import { PublicLayout } from "@/components/PublicLayout";
import { Seo } from "@/components/Seo";
import { GoogleReviewsSection } from "@/components/GoogleReviews";
import { Button } from "@/components/ui/button";
import { ArrowRight, MapPin, Languages, Clock, CreditCard, Users, CheckCircle2 } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1571902943202-507ec2618e8f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const FAQS = [
  ["What is a functional fitness center?", "A gym where you train movements, not machines: squat, hinge, push, pull, carry and rotate, with progressive loads and a coach in every class. At Frenchies in Coronado every session blends strength, mobility and conditioning."],
  ["Where exactly is the gym?", "Inside the Coronado Padel Center in Nueva Gorgona, right off the Interamericana, minutes from Coronado, Gorgona, Playa Serena and San Carlos."],
  ["Are classes available in English?", "Yes. Our coaches speak English, Spanish and French. The booking app and support are available in English too."],
  ["What is the schedule?", "Open every day 7:00 am – 7:00 pm. Group classes Monday to Friday at 7:00 am, 8:30 am and 6:00 pm, Saturdays at 9:00 am. Private training and Pilates Reformer by appointment across opening hours."],
  ["Do you offer short-term packs?", "Yes. Drop-in classes, 5/10-class packs and monthly memberships with no long commitment."],
  ["How do I pay?", "Online by credit card through the app (Paguelo Fácil). You receive a Panamanian electronic invoice (DGI) automatically."],
];

const JSON_LD = {
  "@type": "FAQPage",
  mainEntity: FAQS.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
};
const CRUMBS = [{ name: "Functional Fitness Center Coronado", path: "/functional-fitness-center-coronado" }];

export default function FunctionalFitnessCenter() {
  const navigate = useNavigate();
  const PERKS = [
    [Users, "A coach in every class — strength, mobility & conditioning"],
    [Clock, "Open every day 7 am – 7 pm, classes at 7:00, 8:30 & 6:00 pm"],
    [Languages, "Coaches speak English, Spanish and French"],
    [CreditCard, "Book & pay online in minutes, no long contracts"],
  ];
  return (
    <PublicLayout>
      <Seo
        title="Your Functional Movement Gym in Coronado, Panama — Frenchies Functional Fitness Center"
        description="Frenchies is the functional fitness center of Coronado, Panama: functional movement training, strength, mobility, Pilates Reformer and private coaching. Coaches in English, Spanish & French. Open daily 7am–7pm."
        path="/functional-fitness-center-coronado"
        keywords="functional fitness center Coronado, functional movement gym Coronado, gym Coronado Panama, gym in Coronado, functional training Panama, Pilates Coronado Panama, gym Gorgona Panama, gym near Playa Coronado"
        jsonLd={JSON_LD}
        breadcrumbs={CRUMBS}
      />
      <section className="relative flex min-h-[70vh] w-full items-center overflow-hidden bg-black">
        <img src={HERO_IMG} alt="Functional movement gym in Coronado, Panama — Frenchies Fitness" fetchPriority="high" className="absolute inset-0 h-full w-full object-cover opacity-45" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/30" />
        <div className="relative z-10 mx-auto w-full max-w-4xl px-5 py-20 lg:px-8">
          <div className="mb-3 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-gold"><MapPin className="h-4 w-4" /> Coronado · Nueva Gorgona · Panamá Oeste</div>
          <h1 className="font-display text-4xl font-black uppercase leading-[0.95] tracking-tight text-white sm:text-5xl lg:text-6xl">Your functional movement gym in Coronado</h1>
          <p className="mt-5 max-w-xl text-base text-white/85 md:text-lg">Frenchies is the functional fitness center of Panama's Pacific coast: coached strength, mobility and conditioning, plus Pilates Reformer — for every age and level.</p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button size="lg" data-testid="ffc-book-btn" onClick={() => navigate("/schedule")} className="rounded-full">Book a trial class <ArrowRight className="ml-2 h-4 w-4" /></Button>
            <Button size="lg" variant="outline" data-testid="ffc-pricing-btn" onClick={() => navigate("/pricing")} className="rounded-full border-white/40 bg-white/10 text-white hover:bg-white/20">See packs & memberships</Button>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-5 py-16 lg:px-8">
        <h2 className="font-display text-2xl font-bold tracking-tight md:text-3xl">A functional fitness center built around how you move</h2>
        <p className="mt-4 leading-relaxed text-muted-foreground">
          Frenchies Fitness & Pilates is a premium gym in the Coronado Padel Center (Nueva Gorgona). We don't line you up on machines: every class is coached and built on functional movement — the patterns you use every day, loaded progressively so you get stronger, more mobile and more resilient. It's the ideal training for padel, surf, golf and simply living well in Coronado. Whether you're 25 or 70, a beginner or a former athlete, the coach scales each session to you. Book from your phone, pay by card, cancel within the policy window — no paperwork, no long contracts.
        </p>
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          {PERKS.map(([Icon, txt]) => (
            <div key={txt} className="flex items-center gap-3 rounded-2xl border border-border bg-card px-5 py-4"><Icon className="h-6 w-6 shrink-0 text-primary" /><span className="font-medium">{txt}</span></div>
          ))}
        </div>
        <ul className="mt-8 space-y-3">
          {["Functional movement group classes, 3 levels (Rookie, Fitness, Legend)", "Pilates Reformer group & private sessions", "Kids & teens programs (Baby 3-5, Kids 6-11, Teens 12-17)", "Private coaching and online coaching when you travel"].map((t) => (
            <li key={t} className="flex items-start gap-3"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" /><span className="text-muted-foreground">{t}</span></li>
          ))}
        </ul>
      </section>

      <section className="bg-secondary/40 py-16">
        <div className="mx-auto max-w-4xl px-5 lg:px-8">
          <h2 className="mb-6 font-display text-2xl font-bold tracking-tight md:text-3xl">FAQ</h2>
          <div className="space-y-5">
            {FAQS.map(([q, a], i) => (
              <div key={q} data-testid={`ffc-faq-${i}`} className="rounded-2xl border border-border bg-card p-6">
                <h3 className="font-display text-lg font-semibold">{q}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <GoogleReviewsSection compact title="What our members say on Google" />

      <section className="mx-auto max-w-5xl px-5 py-16 text-center lg:px-8">
        <h2 className="font-display text-3xl font-bold tracking-tight md:text-4xl">Come train with us this week</h2>
        <p className="mt-3 text-muted-foreground">Coronado Padel Center, Nueva Gorgona · WhatsApp +507 6800-4547</p>
        <Button size="lg" data-testid="ffc-cta-btn" onClick={() => navigate("/schedule")} className="mt-7 rounded-full">Book my class <ArrowRight className="ml-2 h-4 w-4" /></Button>
      </section>
    </PublicLayout>
  );
}
